// src/pages/LiveConversation/LiveConversation.js
import React, { useState, useEffect, useCallback } from 'react';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import axiosInstance from '../../utils/axiosInstance';
import { useChatbots } from '../../contexts/ChatbotContext';
import { hasPermission } from '../../utils/permissionUtils';
import TiptapEditor from '../../components/Common/TiptapEditor';

import {
    Box,
    Typography,
    Alert,
    Button,
    Switch,
    FormControlLabel,
    TextField,
    CircularProgress,
    Card,
    CardContent,
    Tabs,
    Tab,
    Checkbox,
    LinearProgress,
    Chip,
    IconButton,
    Tooltip,
    Divider,
    InputAdornment,
} from '@mui/material';
import {
    Save as SaveIcon,
    Language as WebIcon,
    Description as DocIcon,
    CloudQueue as DriveIcon,
    Edit as EditIcon,
    Search as SearchIcon,
    Delete as DeleteIcon,
    CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';

const MAX_CHARACTERS = 20000;

export default function LiveConversation() {
    const { activeChatbot } = useChatbots();
    const chatbotId = activeChatbot?.chatbot_identity;

    // Permission states
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // Data states
    const [ragEnabled, setRagEnabled] = useState(true);
    const [promptText, setPromptText] = useState('');
    const [selectedSources, setSelectedSources] = useState([]);
    const [availableSources, setAvailableSources] = useState([]);
    const [sourceTab, setSourceTab] = useState(0);
    const [searchText, setSearchText] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadingSources, setLoadingSources] = useState(false);
    const [message, setMessage] = useState(null);
    const [totalSourcePage, setTotalSourcePage] = useState(0);
    const [sourcePage, setSourcePage] = useState(1);

    const sourceTypes = ['website', 'document', 'gdrive'];
    const sourceTabLabels = ['Website', 'Documents', 'Google Drive'];
    const sourceTabIcons = [<WebIcon />, <DocIcon />, <DriveIcon />];

    // Tiptap editor for manual content
    const editor = useEditor({
        extensions: [StarterKit, Underline],
        content: '',
        editorProps: {
            attributes: {
                class: 'tiptap-editor-area',
            },
        },
    });

    // Calculate total character count
    const manualContentLength = editor ? editor.getText().length : 0;
    const selectedSourcesChars = selectedSources.reduce((sum, s) => sum + (s.character_count || 0), 0);
    const totalCharacters = manualContentLength + selectedSourcesChars;
    const characterPercentage = Math.min((totalCharacters / MAX_CHARACTERS) * 100, 100);

    // Check permissions
    useEffect(() => {
        const checkPermissions = async () => {
            setCheckingPermissions(true);
            try {
                const viewPerm = await hasPermission('live-conversation', 'view');
                const modifyPerm = await hasPermission('live-conversation', 'add|edit');
                setCanView(viewPerm);
                setCanModify(modifyPerm);
            } catch (error) {
                console.error('Error checking permissions:', error);
                setCanView(false);
                setCanModify(false);
            } finally {
                setCheckingPermissions(false);
            }
        };
        checkPermissions();
    }, []);

    // Fetch existing settings
    useEffect(() => {
        if (!chatbotId || !canView) return;
        const fetchSettings = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-conversation`);
                const data = response.data?.data;
                if (data) {
                    setRagEnabled(data.rag_enabled);
                    setPromptText(data.prompt_text || '');
                    setSelectedSources(data.sources || []);
                    if (editor && data.manual_content) {
                        editor.commands.setContent(data.manual_content);
                    }
                }
            } catch (error) {
                console.error('Error fetching live conversation settings:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, [chatbotId, canView]);

    // Set editor content when editor becomes available and we have saved content
    useEffect(() => {
        if (editor && !loading) {
            // Editor ready - content already set during fetch
        }
    }, [editor, loading]);

    // Fetch available sources when tab or search changes
    const fetchAvailableSources = useCallback(async (page = 1) => {
        if (!chatbotId) return;
        setLoadingSources(true);
        try {
            const response = await axiosInstance.get(`/api/chatbots/${chatbotId}/live-conversation/sources`, {
                params: {
                    source_type: sourceTypes[sourceTab],
                    page,
                    limit: 20,
                    search: searchText,
                }
            });
            setAvailableSources(response.data?.data || []);
            setTotalSourcePage(Math.ceil((response.data?.total || 0) / 20));
            setSourcePage(page);
        } catch (error) {
            console.error('Error fetching available sources:', error);
            setAvailableSources([]);
        } finally {
            setLoadingSources(false);
        }
    }, [chatbotId, sourceTab, searchText]);

    useEffect(() => {
        if (chatbotId && canView) {
            fetchAvailableSources(1);
        }
    }, [chatbotId, canView, sourceTab, searchText, fetchAvailableSources]);

    // Check if a source is already selected
    const isSourceSelected = (sourceType, sourceId) => {
        return selectedSources.some(s => s.source_type === sourceType && s.source_id === sourceId);
    };

    // Toggle source selection
    const handleToggleSource = (source) => {
        const alreadySelected = isSourceSelected(source.source_type, source.source_id);

        if (alreadySelected) {
            setSelectedSources(prev => prev.filter(
                s => !(s.source_type === source.source_type && s.source_id === source.source_id)
            ));
        } else {
            // Check character limit
            const newTotal = totalCharacters + (source.character_count || 0);
            if (newTotal > MAX_CHARACTERS) {
                setMessage({ type: 'error', text: `Adding this source would exceed the ${MAX_CHARACTERS.toLocaleString()} character limit.` });
                return;
            }
            setSelectedSources(prev => [...prev, {
                source_type: source.source_type,
                source_id: source.source_id,
                source_title: source.source_title,
                content: source.content || '',
                character_count: source.character_count || 0,
            }]);
        }
    };

    // Remove a selected source
    const handleRemoveSource = (sourceType, sourceId) => {
        setSelectedSources(prev => prev.filter(
            s => !(s.source_type === sourceType && s.source_id === sourceId)
        ));
    };

    // Save settings
    const handleSave = async () => {
        if (!chatbotId) return;
        setSaving(true);
        setMessage(null);

        try {
            const manualContent = editor ? editor.getHTML() : '';
            const payload = {
                rag_enabled: ragEnabled,
                prompt_text: promptText,
                manual_content: manualContent,
                sources: selectedSources.map(s => ({
                    source_type: s.source_type,
                    source_id: s.source_id,
                    source_title: s.source_title,
                    content: s.content || '',
                    character_count: s.character_count || 0,
                })),
            };

            const response = await axiosInstance.post(`/api/chatbots/${chatbotId}/live-conversation`, payload);

            if (response.data?.success) {
                setMessage({ type: 'success', text: 'Live conversation settings saved successfully.' });
            } else {
                setMessage({ type: 'error', text: response.data?.message || 'Failed to save settings.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: error.response?.data?.message || 'An error occurred while saving.' });
        } finally {
            setSaving(false);
        }
    };

    // Permission checks
    if (checkingPermissions) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canView) {
        return (
            <Box sx={{ p: 4 }}>
                <Alert severity="error" sx={{ mt: 6 }}>
                    <strong>Access Denied</strong><br />
                    You don't have permission to view Live Conversation settings. Please contact your administrator.
                </Alert>
            </Box>
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading settings...</Typography>
            </Box>
        );
    }

    const getSourceTypeIcon = (type) => {
        switch (type) {
            case 'website': return <WebIcon fontSize="small" />;
            case 'document': return <DocIcon fontSize="small" />;
            case 'gdrive': return <DriveIcon fontSize="small" />;
            default: return <EditIcon fontSize="small" />;
        }
    };

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto', mt:10 }}>
            <Typography variant="h5" fontWeight={600} gutterBottom>
                Live Conversation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Configure data sources and prompts for live conversation mode. Maximum {MAX_CHARACTERS.toLocaleString()} characters across all sources.
            </Typography>

            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            {/* RAG Toggle */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={ragEnabled}
                                onChange={(e) => setRagEnabled(e.target.checked)}
                                disabled={!canModify}
                                color="primary"
                            />
                        }
                        label={
                            <Box>
                                <Typography variant="subtitle1" fontWeight={600}>Default Knowlegdebase</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {ragEnabled
                                        ? 'Enabled - The chatbot will use Retrieval-Augmented Generation with trained data.'
                                        : 'Disabled - Add data sources below for the chatbot to use in live conversation.'}
                                </Typography>
                            </Box>
                        }
                        sx={{ alignItems: 'flex-start', m: 0 }}
                    />
                </CardContent>
            </Card>

            {/* Character Counter */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">
                            Total Characters Used
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            color={totalCharacters > MAX_CHARACTERS ? 'error' : 'text.secondary'}
                            fontWeight={600}
                        >
                            {totalCharacters.toLocaleString()} / {MAX_CHARACTERS.toLocaleString()}
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={characterPercentage}
                        color={totalCharacters > MAX_CHARACTERS ? 'error' : characterPercentage > 80 ? 'warning' : 'primary'}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
                        <Typography variant="caption" color="text.secondary">
                            Sources: {selectedSourcesChars.toLocaleString()} chars ({selectedSources.length} items)
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Manual: {manualContentLength.toLocaleString()} chars
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            {/* Data Source Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Data Sources
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select existing trained data from Website, Documents, or Google Drive to include in live conversation.
                    </Typography>

                    {/* Selected Sources */}
                    {selectedSources.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                Selected Sources ({selectedSources.length})
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedSources.map((src, idx) => (
                                    <Chip
                                        key={`${src.source_type}-${src.source_id}-${idx}`}
                                        icon={getSourceTypeIcon(src.source_type)}
                                        label={`${src.source_title?.substring(0, 40)}${src.source_title?.length > 40 ? '...' : ''} (${(src.character_count || 0).toLocaleString()} chars)`}
                                        onDelete={canModify ? () => handleRemoveSource(src.source_type, src.source_id) : undefined}
                                        color="primary"
                                        variant="outlined"
                                        size="small"
                                    />
                                ))}
                            </Box>
                            <Divider sx={{ mt: 2 }} />
                        </Box>
                    )}

                    {/* Source Tabs */}
                    <Tabs
                        value={sourceTab}
                        onChange={(e, v) => { setSourceTab(v); setSearchText(''); }}
                        sx={{ mb: 2 }}
                    >
                        {sourceTabLabels.map((label, idx) => (
                            <Tab key={label} icon={sourceTabIcons[idx]} label={label} iconPosition="start" />
                        ))}
                    </Tabs>

                    {/* Search */}
                    <TextField
                        fullWidth
                        size="small"
                        placeholder={`Search ${sourceTabLabels[sourceTab]}...`}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {/* Source List */}
                    {loadingSources ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress size={30} />
                        </Box>
                    ) : availableSources.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                                No trained {sourceTabLabels[sourceTab].toLowerCase()} data found for this chatbot.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ maxHeight: 350, overflowY: 'auto' }}>
                                {availableSources.map((source) => {
                                    const selected = isSourceSelected(source.source_type, source.source_id);
                                    return (
                                        <Box
                                            key={`${source.source_type}-${source.source_id}`}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                p: 1.5,
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                backgroundColor: selected ? 'action.selected' : 'transparent',
                                                '&:hover': { backgroundColor: 'action.hover' },
                                                cursor: canModify ? 'pointer' : 'default',
                                            }}
                                            onClick={() => canModify && handleToggleSource(source)}
                                        >
                                            <Checkbox
                                                checked={selected}
                                                disabled={!canModify}
                                                size="small"
                                                sx={{ mr: 1 }}
                                            />
                                            {selected && <CheckCircleIcon color="primary" sx={{ mr: 1, fontSize: 18 }} />}
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography variant="body2" noWrap title={source.source_title}>
                                                    {source.source_title}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                label={`${(source.character_count || 0).toLocaleString()} chars`}
                                                size="small"
                                                variant="outlined"
                                                sx={{ ml: 1 }}
                                            />
                                        </Box>
                                    );
                                })}
                            </Box>
                            {/* Pagination */}
                            {totalSourcePage > 1 && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                                    <Button
                                        size="small"
                                        disabled={sourcePage <= 1}
                                        onClick={() => fetchAvailableSources(sourcePage - 1)}
                                    >
                                        Previous
                                    </Button>
                                    <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                                        Page {sourcePage} of {totalSourcePage}
                                    </Typography>
                                    <Button
                                        size="small"
                                        disabled={sourcePage >= totalSourcePage}
                                        onClick={() => fetchAvailableSources(sourcePage + 1)}
                                    >
                                        Next
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Manual Content Editor */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Manual Content
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add custom text content for the live conversation. This counts toward the {MAX_CHARACTERS.toLocaleString()} character limit.
                    </Typography>
                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                        <TiptapEditor editor={editor} height="200px" />
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        {manualContentLength.toLocaleString()} characters
                    </Typography>
                </CardContent>
            </Card>

            {/* Prompt Section */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        Prompt
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Provide instructions for how the chatbot should behave during live conversation.
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={6}
                        value={promptText}
                        onChange={(e) => setPromptText(e.target.value)}
                        placeholder="Enter your prompt instructions here..."
                        disabled={!canModify}
                        variant="outlined"
                    />
                </CardContent>
            </Card>

            {/* Save Button */}
            {canModify && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                        onClick={handleSave}
                        disabled={saving || totalCharacters > MAX_CHARACTERS}
                    >
                        {saving ? 'Saving...' : 'Save Settings'}
                    </Button>
                </Box>
            )}
        </Box>
    );
}
